import React, { useState, useRef } from 'react';
import {
  Truck, Shield, Wrench, MapPin, AlertTriangle,
  Archive, Camera, CheckCircle, Plus, QrCode
} from 'lucide-react';
import {
  ASSET_TYPES, ASSET_CATEGORIES, ASSET_STATUSES, ASSET_CONDITIONS,
  FUEL_TYPES, LOSS_TYPES, INSPECTION_RESULTS, DECOMMISSION_REASONS,
  REGIONS, UNITS, generateAssetId, generateQRCode, ASSETS_API_URL,
} from '../../config/assets';

const inp = "w-full px-3 py-2.5 bg-slate-700/60 border border-slate-600/40 rounded-lg text-white text-sm focus:border-blue-500/60 focus:outline-none focus:ring-1 focus:ring-blue-500/20 transition-all";
const sel = inp;
const lbl = "block text-xs font-medium text-blue-300 mb-1.5";

const Section = ({ icon: Icon, title, children }: any) => (
  <div className="rounded-xl border border-blue-900/40 bg-slate-800/80 overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-3 bg-slate-700/60 border-b border-blue-900/40">
      <Icon className="w-4 h-4 text-blue-400"/>
      <span className="text-sm font-semibold text-white">{title}</span>
    </div>
    <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
  </div>
);

const Field = ({ label, children, span = 1 }: any) => (
  <div className={span === 2 ? 'md:col-span-2' : span === 3 ? 'md:col-span-3' : ''}>
    <label className={lbl}>{label}</label>
    {children}
  </div>
);

export const AssetForm = ({ user }: { user: any }) => {
  const now = new Date().toISOString().slice(0, 10);
  const initId  = generateAssetId();
  const initQR  = generateQRCode(initId);

  const [form, setForm] = useState({
    asset_id: initId, qr_code: initQR,
    asset_type:'', category:'', brand:'', model:'', serial_number:'',
    year_manufactured:'', vehicle_registration:'',
    region:'', unit:'', assigned_officer:'', assigned_officer_id:'',
    assigned_city:'', assigned_precinct:'', assigned_email:'',
    purchase_date:'', purchase_cost:'',
    status:'Opérationnel', condition:'Bon', operational_since: now,
    last_seen_date: now, last_seen_location:'', gps_latitude:'', gps_longitude:'',
    current_mileage:'', operational_hours:'', fuel_type:'Diesel',
    fuel_capacity_l:'', last_fuel_date:'', last_fuel_quantity_l:'',
    fuel_consumption_liters:'', fuel_expected_liters:'',
    engine_temp_warning:'FALSE', oil_pressure_warning:'FALSE', brake_wear_pct:'',
    last_maintenance_date:'', maintenance_cost_ytd:'',
    last_inspection_date:'', next_inspection_date:'', inspection_officer:'',
    inspection_result:'', inspection_notes:'', inspection_attachment_url:'',
    repair_date:'', repair_type:'', repair_description:'', repair_cost:'',
    repair_technician:'', repair_provider:'', repair_duration_days:'',
    loss_date:'', loss_type:'', loss_description:'', loss_report_ref:'',
    loss_declared_by:'',
    decommission_date:'', decommission_reason:'', decommission_approved_by:'',
    decommission_notes:'', replacement_asset_id:'',
    created_at: now, updated_at: now,
    created_by: user?.name || user?.username || '',
    asset_notes:'',
  });

  const [photo, setPhoto]       = useState<File | null>(null);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState('');
  const photoRef = useRef<HTMLInputElement>(null);
  const attachRef = useRef<HTMLInputElement>(null);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.asset_type || !form.region || !form.unit) {
      setError('Veuillez remplir les champs obligatoires : Type, Région, Unité.');
      return;
    }
    setSubmitting(true); setError('');
    try {
      const payload = {
        ...form,
        _type: 'asset',
        updated_at: new Date().toISOString().slice(0,10),
        inspection_attachment_url: attachment ? attachment.name : form.inspection_attachment_url,
      };
      const sheetsUrl = (import.meta.env.VITE_SHEETS_API_URL || '').replace('?type=asset','');
      const res  = await fetch(sheetsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) setSubmitted(true);
      else setError(data.error || 'Erreur lors de la soumission.');
    } catch {
      setError('Impossible de contacter le serveur.');
    } finally { setSubmitting(false); }
  };

  if (submitted) return (
    <div className="rounded-xl border border-green-500/30 bg-slate-800/80 p-8 text-center">
      <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3"/>
      <h3 className="text-white font-bold text-lg mb-1">Actif enregistré avec succès</h3>
      <p className="text-slate-300 text-sm mb-1">Référence : <span className="font-mono text-blue-300">{form.asset_id}</span></p>
      <p className="text-slate-400 text-xs mb-4">QR Code : <span className="font-mono">{form.qr_code}</span></p>
      <button onClick={() => { setSubmitted(false); setForm(f => ({ ...f, asset_id: generateAssetId(), qr_code: generateQRCode(generateAssetId()) })); }}
        className="px-6 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white text-sm font-medium transition-colors">
        Nouvel actif
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-400 text-sm">{error}</div>
      )}

      {/* Header */}
      <div className="rounded-xl border border-blue-900/40 bg-slate-800/60 p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-white font-bold text-base">Fiche d'enregistrement d'actif</h2>
          <p className="text-blue-300 text-xs mt-0.5">NEXUS360 · Gestion Logistique · DGSN</p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5 bg-slate-700/60 rounded-lg px-3 py-1.5 border border-blue-900/40">
            <QrCode className="w-3.5 h-3.5 text-blue-400"/>
            <span className="text-blue-300 font-mono">{form.asset_id}</span>
          </div>
          <span className="text-slate-400">{now}</span>
        </div>
      </div>

      {/* Section 1: Identity */}
      <Section icon={Truck} title="1. Identification de l'actif">
        <Field label="Type d'actif *">
          <select value={form.asset_type} onChange={e => set('asset_type', e.target.value)} className={sel}>
            <option value="">Sélectionner...</option>
            {ASSET_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Catégorie">
          <select value={form.category} onChange={e => set('category', e.target.value)} className={sel}>
            <option value="">Sélectionner...</option>
            {ASSET_CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Immatriculation / Référence">
          <input value={form.vehicle_registration} onChange={e => set('vehicle_registration', e.target.value)} className={inp} placeholder="LT-0000-X"/>
        </Field>
        <Field label="Marque">
          <input value={form.brand} onChange={e => set('brand', e.target.value)} className={inp} placeholder="Toyota, Yamaha..."/>
        </Field>
        <Field label="Modèle">
          <input value={form.model} onChange={e => set('model', e.target.value)} className={inp} placeholder="Land Cruiser..."/>
        </Field>
        <Field label="Numéro de série">
          <input value={form.serial_number} onChange={e => set('serial_number', e.target.value)} className={inp}/>
        </Field>
        <Field label="Année de fabrication">
          <input type="number" value={form.year_manufactured} onChange={e => set('year_manufactured', e.target.value)} className={inp} placeholder="2020"/>
        </Field>
        <Field label="Date d'acquisition">
          <input type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} className={inp} style={{colorScheme:'dark'}}/>
        </Field>
        <Field label="Coût d'acquisition (FCFA)">
          <input type="number" value={form.purchase_cost} onChange={e => set('purchase_cost', e.target.value)} className={inp}/>
        </Field>
      </Section>

      {/* Section 2: Assignment */}
      <Section icon={Shield} title="2. Affectation">
        <Field label="Région *">
          <select value={form.region} onChange={e => set('region', e.target.value)} className={sel}>
            <option value="">Sélectionner...</option>
            {REGIONS.map(r => <option key={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Unité / Brigade *">
          <select value={form.unit} onChange={e => set('unit', e.target.value)} className={sel}>
            <option value="">Sélectionner...</option>
            {UNITS.map(u => <option key={u}>{u}</option>)}
          </select>
        </Field>
        <Field label="Ville">
          <input value={form.assigned_city} onChange={e => set('assigned_city', e.target.value)} className={inp}/>
        </Field>
        <Field label="Commissariat affecté">
          <input value={form.assigned_precinct} onChange={e => set('assigned_precinct', e.target.value)} className={inp}/>
        </Field>
        <Field label="Officier responsable">
          <input value={form.assigned_officer} onChange={e => set('assigned_officer', e.target.value)} className={inp}/>
        </Field>
        <Field label="ID Officier">
          <input value={form.assigned_officer_id} onChange={e => set('assigned_officer_id', e.target.value)} className={inp} placeholder="OFF-00000"/>
        </Field>
        <Field label="Email officier">
          <input type="email" value={form.assigned_email} onChange={e => set('assigned_email', e.target.value)} className={inp}/>
        </Field>
        <Field label="En service depuis">
          <input type="date" value={form.operational_since} onChange={e => set('operational_since', e.target.value)} className={inp} style={{colorScheme:'dark'}}/>
        </Field>
      </Section>

      {/* Section 3: Status & Location */}
      <Section icon={MapPin} title="3. Statut, Condition & Géolocalisation">
        <Field label="Statut opérationnel">
          <select value={form.status} onChange={e => set('status', e.target.value)} className={sel}>
            {ASSET_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Condition générale">
          <select value={form.condition} onChange={e => set('condition', e.target.value)} className={sel}>
            {ASSET_CONDITIONS.map(c => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Dernière date de localisation">
          <input type="date" value={form.last_seen_date} onChange={e => set('last_seen_date', e.target.value)} className={inp} style={{colorScheme:'dark'}}/>
        </Field>
        <Field label="Dernière localisation connue" span={2}>
          <input value={form.last_seen_location} onChange={e => set('last_seen_location', e.target.value)} className={inp} placeholder="Quartier, ville, route..."/>
        </Field>
        <Field label="Latitude GPS">
          <input type="number" step="0.000001" value={form.gps_latitude} onChange={e => set('gps_latitude', e.target.value)} className={inp} placeholder="3.848000"/>
        </Field>
        <Field label="Longitude GPS">
          <input type="number" step="0.000001" value={form.gps_longitude} onChange={e => set('gps_longitude', e.target.value)} className={inp} placeholder="11.502000"/>
        </Field>
      </Section>

      {/* Section 4: Usage & Fuel */}
      <Section icon={Truck} title="4. Utilisation & Carburant">
        <Field label="Kilométrage actuel (km)">
          <input type="number" value={form.current_mileage} onChange={e => set('current_mileage', e.target.value)} className={inp}/>
        </Field>
        <Field label="Heures opérationnelles">
          <input type="number" value={form.operational_hours} onChange={e => set('operational_hours', e.target.value)} className={inp}/>
        </Field>
        <Field label="Type de carburant">
          <select value={form.fuel_type} onChange={e => set('fuel_type', e.target.value)} className={sel}>
            {FUEL_TYPES.map(f => <option key={f}>{f}</option>)}
          </select>
        </Field>
        <Field label="Capacité réservoir (L)">
          <input type="number" value={form.fuel_capacity_l} onChange={e => set('fuel_capacity_l', e.target.value)} className={inp}/>
        </Field>
        <Field label="Consommation réelle (L)">
          <input type="number" step="0.1" value={form.fuel_consumption_liters} onChange={e => set('fuel_consumption_liters', e.target.value)} className={inp}/>
        </Field>
        <Field label="Consommation attendue (L)">
          <input type="number" step="0.1" value={form.fuel_expected_liters} onChange={e => set('fuel_expected_liters', e.target.value)} className={inp}/>
        </Field>
        <Field label="Dernier ravitaillement">
          <input type="date" value={form.last_fuel_date} onChange={e => set('last_fuel_date', e.target.value)} className={inp} style={{colorScheme:'dark'}}/>
        </Field>
        <Field label="Quantité ravitaillement (L)">
          <input type="number" step="0.1" value={form.last_fuel_quantity_l} onChange={e => set('last_fuel_quantity_l', e.target.value)} className={inp}/>
        </Field>
        <Field label="Coût maintenance annuel (FCFA)">
          <input type="number" value={form.maintenance_cost_ytd} onChange={e => set('maintenance_cost_ytd', e.target.value)} className={inp}/>
        </Field>
        <div className="flex flex-col gap-3">
          <label className={lbl}>Alertes techniques</label>
          <div className="flex flex-col gap-2">
            {[
              { key: 'engine_temp_warning', label: '🌡 Alerte température moteur' },
              { key: 'oil_pressure_warning', label: '🛢 Alerte pression huile' },
            ].map(a => (
              <label key={a.key} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox"
                  checked={form[a.key as keyof typeof form] === 'TRUE'}
                  onChange={e => set(a.key, e.target.checked ? 'TRUE' : 'FALSE')}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-500"/>
                <span className="text-xs text-slate-300">{a.label}</span>
              </label>
            ))}
            <label className="block text-xs text-blue-300 mt-1">🔧 Usure des freins (%)</label>
            <input type="number" min="0" max="100"
              value={form.brake_wear_pct} onChange={e => set('brake_wear_pct', e.target.value)}
              className={inp} placeholder="0-100"/>
          </div>
        </div>
      </Section>

      {/* Section 5: Mechanical Inspection */}
      <Section icon={Wrench} title="5. Inspection mécanique">
        <Field label="Date dernière inspection">
          <input type="date" value={form.last_inspection_date} onChange={e => set('last_inspection_date', e.target.value)} className={inp} style={{colorScheme:'dark'}}/>
        </Field>
        <Field label="Date prochaine inspection">
          <input type="date" value={form.next_inspection_date} onChange={e => set('next_inspection_date', e.target.value)} className={inp} style={{colorScheme:'dark'}}/>
        </Field>
        <Field label="Officier inspecteur">
          <input value={form.inspection_officer} onChange={e => set('inspection_officer', e.target.value)} className={inp}/>
        </Field>
        <Field label="Résultat d'inspection">
          <select value={form.inspection_result} onChange={e => set('inspection_result', e.target.value)} className={sel}>
            <option value="">Sélectionner...</option>
            {INSPECTION_RESULTS.map(r => <option key={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Notes d'inspection" span={2}>
          <textarea value={form.inspection_notes} onChange={e => set('inspection_notes', e.target.value)}
            className={inp + ' resize-none h-20'} placeholder="Observations techniques..."/>
        </Field>
        <Field label="Pièce jointe — Rapport d'inspection" span={3}>
          <div className="border-2 border-dashed border-blue-800/60 rounded-lg p-4 bg-slate-700/30">
            {attachment ? (
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400"/>
                <span className="text-sm text-green-400">{attachment.name}</span>
                <button onClick={() => setAttachment(null)} className="ml-auto text-red-400 text-xs hover:text-red-300">✕ Retirer</button>
              </div>
            ) : (
              <div className="text-center">
                <Camera className="w-6 h-6 text-blue-400/60 mx-auto mb-2"/>
                <p className="text-xs text-slate-400 mb-2">PDF, JPG, PNG · max 10MB</p>
                <label className="px-4 py-1.5 bg-slate-600 hover:bg-slate-500 rounded-lg cursor-pointer text-xs text-white transition-colors inline-flex items-center gap-2">
                  <input ref={attachRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                    onChange={e => setAttachment(e.target.files?.[0] || null)}/>
                  Joindre le rapport
                </label>
              </div>
            )}
          </div>
        </Field>
      </Section>

      {/* Section 6: Repair */}
      <Section icon={Wrench} title="6. Réparation">
        <Field label="Date de réparation">
          <input type="date" value={form.repair_date} onChange={e => set('repair_date', e.target.value)} className={inp} style={{colorScheme:'dark'}}/>
        </Field>
        <Field label="Type de réparation">
          <input value={form.repair_type} onChange={e => set('repair_type', e.target.value)} className={inp} placeholder="Moteur, transmission, carrosserie..."/>
        </Field>
        <Field label="Coût (FCFA)">
          <input type="number" value={form.repair_cost} onChange={e => set('repair_cost', e.target.value)} className={inp}/>
        </Field>
        <Field label="Technicien">
          <input value={form.repair_technician} onChange={e => set('repair_technician', e.target.value)} className={inp}/>
        </Field>
        <Field label="Prestataire">
          <input value={form.repair_provider} onChange={e => set('repair_provider', e.target.value)} className={inp}/>
        </Field>
        <Field label="Durée (jours)">
          <input type="number" value={form.repair_duration_days} onChange={e => set('repair_duration_days', e.target.value)} className={inp}/>
        </Field>
        <Field label="Description" span={3}>
          <textarea value={form.repair_description} onChange={e => set('repair_description', e.target.value)}
            className={inp + ' resize-none h-20'} placeholder="Nature des travaux effectués..."/>
        </Field>
      </Section>

      {/* Section 7: Loss / Theft */}
      <Section icon={AlertTriangle} title="7. Perte / Vol / Destruction">
        <Field label="Date de perte/vol">
          <input type="date" value={form.loss_date} onChange={e => set('loss_date', e.target.value)} className={inp} style={{colorScheme:'dark'}}/>
        </Field>
        <Field label="Type">
          <select value={form.loss_type} onChange={e => set('loss_type', e.target.value)} className={sel}>
            <option value="">Sélectionner...</option>
            {LOSS_TYPES.map(l => <option key={l}>{l}</option>)}
          </select>
        </Field>
        <Field label="Réf. rapport de perte">
          <input value={form.loss_report_ref} onChange={e => set('loss_report_ref', e.target.value)} className={inp} placeholder="RPT-000000"/>
        </Field>
        <Field label="Déclaré par">
          <input value={form.loss_declared_by} onChange={e => set('loss_declared_by', e.target.value)} className={inp}/>
        </Field>
        <Field label="Description" span={2}>
          <textarea value={form.loss_description} onChange={e => set('loss_description', e.target.value)}
            className={inp + ' resize-none h-20'} placeholder="Circonstances de la perte..."/>
        </Field>
      </Section>

      {/* Section 8: Decommission */}
      <Section icon={Archive} title="8. Déclassement">
        <Field label="Date de déclassement">
          <input type="date" value={form.decommission_date} onChange={e => set('decommission_date', e.target.value)} className={inp} style={{colorScheme:'dark'}}/>
        </Field>
        <Field label="Motif">
          <select value={form.decommission_reason} onChange={e => set('decommission_reason', e.target.value)} className={sel}>
            <option value="">Sélectionner...</option>
            {DECOMMISSION_REASONS.map(d => <option key={d}>{d}</option>)}
          </select>
        </Field>
        <Field label="Approuvé par">
          <input value={form.decommission_approved_by} onChange={e => set('decommission_approved_by', e.target.value)} className={inp}/>
        </Field>
        <Field label="Actif de remplacement">
          <input value={form.replacement_asset_id} onChange={e => set('replacement_asset_id', e.target.value)} className={inp} placeholder="AST-00000000"/>
        </Field>
        <Field label="Notes" span={2}>
          <textarea value={form.decommission_notes} onChange={e => set('decommission_notes', e.target.value)}
            className={inp + ' resize-none h-20'} placeholder="Conditions et décision..."/>
        </Field>
      </Section>

      {/* Section 9: Notes */}
      <div className="rounded-xl border border-blue-900/40 bg-slate-800/80 p-4">
        <label className={lbl}>Notes complémentaires</label>
        <textarea value={form.asset_notes} onChange={e => set('asset_notes', e.target.value)}
          className={inp + ' resize-none h-24'} placeholder="Informations additionnelles sur cet actif..."/>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between bg-slate-700/40 rounded-lg p-3 border border-blue-900/30">
        <div className="text-xs text-slate-400 flex flex-wrap gap-4">
          <span>ID: <span className="font-mono text-blue-300">{form.asset_id}</span></span>
          <span>QR: <span className="font-mono text-blue-300">{form.qr_code}</span></span>
          <span>Par: <span className="font-mono text-blue-300">{form.created_by}</span></span>
        </div>
        <button onClick={handleSubmit} disabled={submitting}
          className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg text-white text-sm font-semibold transition-colors shadow-lg">
          {submitting
            ? <><span className="animate-spin border-2 border-white/30 border-t-white rounded-full w-4 h-4"/> Envoi...</>
            : <><Plus className="w-4 h-4"/> Enregistrer l'actif</>
          }
        </button>
      </div>
    </div>
  );
};
